import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Grid, Link } from '@mui/material';
import { TextField, Autocomplete, Button, Typography } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { Prompt } from 'react-router-dom';
import { editableStates } from '../../../../../../constants/editableStates';
import { RightsContext } from 'providers/RightsContext';
import { ParamsContext } from 'providers/ParamsContext';
import { GridActionsWrapper } from 'components/common/CommonStyles';

const FacilityGeneralInfoComponent = ({
  facilityHeader,
  onPropertyChange,
  onSave,
  onTransferFacilityToERP,
  validation,
  loadCurrencies,
  loadPartners,
  facilityRevolvingTypes,
  facilityFlexibilityRules,
  facilityCreditLineStatuses,
  facilityPurposesForApplication,
  partnerGroups,
  partnerOptions,
  transferToERPPermissions,
  isSaving,
  isTransfering,
  isDirty,
  openLinkedDocument,
  editable
}) => {
  const { t } = useTranslation();
  const { facilityConfigurationGeneralInfoWrite = true } = useContext(RightsContext);
  const { cvsChAmount } = useContext(ParamsContext);
  const onAutocompleteChangePartner = onPropertyChange => partner => {
    if (partner || partner === null) onPropertyChange(partner?.partnerId, partner?.partnerName ?? '');
  };

  const onFacilityAmountChange = onPropertyChange => facilityAmount => {
    if (facilityAmount)
      onPropertyChange(
        facilityAmount,
        Math.round(
          (facilityAmount - (facilityHeader.blockedAmount ?? 0) - (facilityHeader.usedAmount ?? 0) + Number.EPSILON) * 10 ** cvsChAmount
        ) /
          10 ** cvsChAmount
      );
  };

  const clientNameValue =
    facilityHeader.clientName && facilityHeader.clientName !== '' ? facilityHeader.clientName : facilityHeader.partnerName;

  return (
    <>
      <Prompt when={isDirty} message={'LeavingWithoutSaving'} />
      <Grid container spacing={2}>
        {facilityHeader.offerId && editable === editableStates.NotEditable && (
          <Grid item xs={12}>
            <span> {t('Facility.OfferNumber')}</span>
            <span>
              <Link style={{ cursor: 'pointer' }} onClick={openLinkedDocument(facilityHeader.offerId, facilityHeader.offerSiteId)}>
                {facilityHeader.offerNumber}
              </Link>
            </span>
            <span>
              {' / ' +
                facilityHeader.offerStateName +
                ' / ' +
                t('DATE_FORMAT', { date: { value: facilityHeader.offerDate, format: 'DD.MM.YYYY' } })}
            </span>
          </Grid>
        )}
        {editable === editableStates.NotEditable && (
          <Grid item xs={12}>
            <Typography>{t('Facility.HelperText')}</Typography>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id='documentNumber'
            value={facilityHeader.documentNumber || ''}
            label={t('Facility.FacilityNumber')}
            onChange={onPropertyChange('documentNumber')}
            error={!isValid(validation.documentNumber)}
            helperText={getErrors(validation.documentNumber)}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DateTime
            id={'documentDate'}
            fullWidth
            value={facilityHeader.documentDate}
            label={t('Facility.DocumentDate')}
            onChange={onPropertyChange('documentDate')}
            error={!isValid(validation.documentDate)}
            helperText={getErrors(validation.documentDate)}
            timeFormat={false}
            disabled={
              editable === editableStates.NotEditable ||
              editable === editableStates.EditableFromSourceDocument ||
              !facilityConfigurationGeneralInfoWrite
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            inputProps={{ decimalScale: cvsChAmount }}
            id='facilityAmount'
            value={facilityHeader.facilityAmount || ''}
            label={t('Facility.FacilityAmount')}
            onChange={onFacilityAmountChange(onPropertyChange('facilityAmount', 'leftAmount'))}
            error={!isValid(validation.facilityAmount)}
            helperText={getErrors(validation.facilityAmount)}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            id='facilityCurrency'
            value={facilityHeader.currencyId}
            label={t('Facility.Currency')}
            labelKey='code'
            valueKey='currencyId'
            loadOptions={loadCurrencies || []}
            simpleValue={true}
            onChange={onPropertyChange('currencyId')}
            isClearable={false}
            error={!isValid(validation.currencyId)}
            helperText={getErrors(validation.currencyId)}
            disabled={
              editable === editableStates.NotEditable ||
              editable === editableStates.EditableFromSourceDocument ||
              !facilityConfigurationGeneralInfoWrite
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            id='facilityPartnerGroup'
            value={facilityHeader.partnerGroupId}
            valueKey='dictionaryItemId'
            label={t('Facility.PartnerGroup')}
            labelKey='dictionaryItemName'
            options={partnerGroups}
            onChange={onPropertyChange('partnerGroupId')}
            simpleValue={true}
            error={!isValid(validation.partnerGroupId)}
            helperText={
              (facilityHeader.clientName && facilityHeader.clientName !== '') || facilityHeader.partnerId
                ? t('Facility.ClientNameMustBeEmpty')
                : undefined
            }
            disabled={
              editable === editableStates.NotEditable ||
              editable === editableStates.EditableFromSourceDocument ||
              (!!facilityHeader.clientName && facilityHeader.clientName !== '') ||
              facilityHeader.partnerId ||
              !facilityConfigurationGeneralInfoWrite
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id='facilityClientName'
            value={clientNameValue || ''}
            label={t('Facility.ClientName')}
            onChange={onPropertyChange('clientName')}
            simpleValue={true}
            helperText={facilityHeader.partnerGroupId ? t('Facility.PartnerGroupMustBeEmpty') : undefined}
            disabled={
              editable === editableStates.NotEditable ||
              editable === editableStates.EditableFromSourceDocument ||
              (facilityHeader.partnerGroupId ? true : false) ||
              !facilityConfigurationGeneralInfoWrite
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            id='facilityPartner'
            value={facilityHeader.partnerId}
            valueKey='partnerId'
            labelKey='partnerUniqueId'
            label={t('Facility.Partner')}
            options={partnerOptions}
            onChange={onAutocompleteChangePartner(onPropertyChange('partnerId', 'partnerName'))}
            simpleValue={true}
            onInputChange={input => loadPartners(input)}
            onSelectResetsInput={false}
            error={!isValid(validation.partnerId)}
            helperText={facilityHeader.partnerGroupId ? t('Facility.PartnerGroupMustBeEmpty') : getErrors(validation.partnerId)}
            disabled={
              editable === editableStates.NotEditable ||
              editable === editableStates.EditableFromSourceDocument ||
              (facilityHeader.partnerGroupId ? true : false) ||
              !facilityConfigurationGeneralInfoWrite
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            id='facilityGroup'
            value={facilityHeader.revolvingItemId}
            label={t('Facility.RevolvingType')}
            options={facilityRevolvingTypes}
            onChange={onPropertyChange('revolvingItemId')}
            simpleValue={true}
            error={!isValid(validation.revolvingItemId)}
            helperText={getErrors(validation.revolvingItemId)}
            isClearable={false}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DateTime
            id={'facilityValidFrom'}
            fullWidth
            value={facilityHeader.validFrom}
            label={t('Facility.ValidFrom')}
            onChange={onPropertyChange('validFrom')}
            error={!isValid(validation.validFrom)}
            helperText={getErrors(validation.validFrom)}
            timeFormat={false}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DateTime
            id={'facilityValidUntil'}
            fullWidth
            value={facilityHeader.validUntil}
            label={t('Facility.ValidUntil')}
            onChange={onPropertyChange('validUntil')}
            error={!isValid(validation.validUntil)}
            helperText={getErrors(validation.validUntil).split(/(T)+[0-9]/)[0]}
            timeFormat={false}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DateTime
            id={'approvalDate'}
            fullWidth
            value={facilityHeader.approvalDate}
            label={t('Facility.ApprovalDate')}
            onChange={onPropertyChange('approvalDate')}
            error={!isValid(validation.approvalDate)}
            helperText={getErrors(validation.approvalDate)}
            timeFormat={false}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <DateTime
            id={'facilityLastTrialBalance'}
            fullWidth
            value={facilityHeader.lastTrialBalance}
            label={t('Facility.LastTrialBalance')}
            onChange={onPropertyChange('lastTrialBalance')}
            error={!isValid(validation.lastTrialBalance)}
            helperText={getErrors(validation.lastTrialBalance)}
            timeFormat={false}
            isClearable
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            isNumeric
            fullWidth
            inputProps={{ decimalScale: 0 }}
            id='facilityValidityPeriod'
            value={facilityHeader.validityPeriod || ''}
            label={t('Facility.ValidityPeriod')}
            onChange={onPropertyChange('validityPeriod')}
            error={!isValid(validation.validityPeriod)}
            helperText={getErrors(validation.validityPeriod)}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            id='facilityFlexibilityRule'
            value={facilityHeader.flexibilityRuleId}
            valueKey='id'
            label={t('Facility.FlexibilityRule')}
            labelKey='name'
            options={facilityFlexibilityRules}
            onChange={onPropertyChange('flexibilityRuleId')}
            simpleValue={true}
            error={!isValid(validation.flexibilityRuleId)}
            helperText={getErrors(validation.flexibilityRuleId)}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            id='facilityCreditLineStatus'
            value={facilityHeader.creditLineStatusId}
            valueKey='id'
            label={t('Facility.CreditLineStatus')}
            labelKey='name'
            options={facilityCreditLineStatuses}
            onChange={onPropertyChange('creditLineStatusId')}
            simpleValue={true}
            error={!isValid(validation.creditLineStatusId)}
            helperText={getErrors(validation.creditLineStatusId)}
            disabled={
              editable === editableStates.NotEditable ||
              editable === editableStates.EditableFromSourceDocument ||
              !facilityConfigurationGeneralInfoWrite
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            id='facilityPurposeForApplication'
            value={facilityHeader.purposeForApplicationId}
            valueKey='id'
            label={t('Facility.PurposeForApplication')}
            labelKey='name'
            options={facilityPurposesForApplication}
            onChange={onPropertyChange('purposeForApplicationId')}
            simpleValue={true}
            error={!isValid(validation.purposeForApplicationId)}
            helperText={getErrors(validation.purposeForApplicationId)}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            isNumeric
            fullWidth
            inputProps={{ decimalScale: cvsChAmount }}
            id='facilityBlockedAmount'
            value={facilityHeader.blockedAmount || ''}
            label={t('Facility.BlockedAmount')}
            disabled={true}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            isNumeric
            fullWidth
            inputProps={{ decimalScale: cvsChAmount }}
            id='facilityUsedAmount'
            value={facilityHeader.usedAmount || ''}
            label={t('Facility.UsedAmount')}
            disabled={true}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            isNumeric
            fullWidth
            inputProps={{ decimalScale: cvsChAmount }}
            id='facilityLeftAmount'
            value={facilityHeader.leftAmount || ''}
            label={t('Facility.LeftAmount')}
            disabled={true}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            isNumeric
            fullWidth
            inputProps={{ decimalScale: cvsChAmount }}
            id='facilityRating'
            value={facilityHeader.rating || ''}
            label={t('Facility.Rating')}
            onChange={onPropertyChange('rating')}
            error={!isValid(validation.rating)}
            helperText={getErrors(validation.rating)}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            isNumeric
            fullWidth
            inputProps={{ decimalScale: 0, thousandSeparator: false }}
            id='facilityRatingYear'
            value={facilityHeader.ratingYear || ''}
            label={t('Facility.RatingYear')}
            onChange={onPropertyChange('ratingYear')}
            error={!isValid(validation.ratingYear)}
            helperText={getErrors(validation.ratingYear)}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        <Grid item xs={12} sm={8} md={12}>
          <TextField
            label={t('Facility.Comment')}
            multiline
            maxRows='2'
            fullWidth
            value={facilityHeader.comments || ''}
            onChange={onPropertyChange('comments')}
            error={!isValid(validation.comments)}
            helperText={getErrors(validation.comments)}
            disabled={editable === editableStates.NotEditable || !facilityConfigurationGeneralInfoWrite}
          />
        </Grid>
        {editable !== editableStates.NotEditable && (
          <GridActionsWrapper item xs={12} mt={1}>
            <Button
              color='primary'
              variant='contained'
              onClick={onSave}
              disabled={!isDirty || !(validation |> isValid) || !facilityConfigurationGeneralInfoWrite}
            >
              {isSaving ? t('Saving') + '...' : t('Save')}
            </Button>
            {transferToERPPermissions.transferToERPIsVisible && (
              <Button
                color='primary'
                variant='contained'
                onClick={onTransferFacilityToERP}
                disabled={
                  !transferToERPPermissions.transferToERPIsEnabled ||
                  isTransfering ||
                  isDirty ||
                  !facilityHeader.documentId ||
                  !(validation |> isValid) ||
                  !facilityConfigurationGeneralInfoWrite
                }
                tooltip={facilityHeader.chDocumentId ? t('Tooltips.UpdateInERP') : t('Tooltips.TransferToERP')}
              >
                {facilityHeader.chDocumentId
                  ? isTransfering
                    ? t('Facility.UpdatingInERP') + '...'
                    : t('Facility.UpdateInERP')
                  : isTransfering
                  ? t('Facility.TransferingToERP') + '...'
                  : t('Facility.TransferToERP')}
              </Button>
            )}
          </GridActionsWrapper>
        )}
      </Grid>
    </>
  );
};

FacilityGeneralInfoComponent.propTypes = {
  facilityHeader: PropTypes.object.isRequired,
  validation: PropTypes.object,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onTransferFacilityToERP: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  isTransfering: PropTypes.bool.isRequired,
  facilityRevolvingTypes: PropTypes.array.isRequired,
  facilityFlexibilityRules: PropTypes.array.isRequired,
  facilityCreditLineStatuses: PropTypes.array.isRequired,
  facilityPurposesForApplication: PropTypes.array.isRequired,
  partnerGroups: PropTypes.array.isRequired,
  partnerOptions: PropTypes.array.isRequired,
  transferToERPPermissions: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired,
  openLinkedDocument: PropTypes.func,
  loadCurrencies: PropTypes.func.isRequired,
  loadPartners: PropTypes.func.isRequired,
  editable: PropTypes.string
};

export default FacilityGeneralInfoComponent;
